import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, ArrowRight, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Motion";
import { SERVICES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Accordion = ({ service, index, open, onToggle }) => {
  return (
    <div className="border-b border-white/10" data-testid={`service-${index}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-6 text-left group"
        data-testid={`service-toggle-${index}`}
      >
        <div className="flex items-center gap-6">
          <span className="font-outfit text-sm text-[#5cc6ff]">{String(index + 1).padStart(2, "0")}</span>
          <h3 className="font-anton uppercase text-2xl md:text-3xl text-white group-hover:text-[#5cc6ff] transition-colors">
            {service.title}
          </h3>
        </div>
        <span className="text-[#5cc6ff]">
          {open ? <Minus strokeWidth={1.5} /> : <Plus strokeWidth={1.5} />}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden"
          >
            <p className="font-manrope text-white/60 pb-6 pl-12 max-w-md">{service.desc}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Field = ({ label, textarea, ...props }) => (
  <div className="relative group">
    <label className="font-outfit text-[10px] uppercase tracking-[0.3em] text-white/40 block mb-2">
      {label}
    </label>
    {textarea ? (
      <textarea
        {...props}
        rows={3}
        className="w-full bg-transparent border-b-2 border-white/20 py-3 font-manrope text-white text-lg focus:outline-none focus:border-[#5cc6ff] transition-colors resize-none placeholder-white/20"
      />
    ) : (
      <input
        {...props}
        className="w-full bg-transparent border-b-2 border-white/20 py-3 font-manrope text-white text-lg focus:outline-none focus:border-[#5cc6ff] transition-colors placeholder-white/20"
      />
    )}
  </div>
);

export const ServicesContact = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. TMB will be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="services" className="relative py-20 md:py-28 max-w-[1600px] mx-auto px-6 md:px-12" data-testid="services-contact-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32">
        <div>
          <Reveal>
            <div className="flex items-center gap-4 mb-10">
              <span className="w-12 h-px bg-[#5cc6ff]" />
              <span className="font-outfit text-xs uppercase tracking-[0.35em] text-white/50">
                What I Shoot
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            {SERVICES.map((s, i) => (
              <Accordion
                key={i}
                service={s}
                index={i}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </Reveal>
        </div>

        <div id="contact" data-testid="contact-section" className="scroll-mt-24">
          <Reveal>
            <h2 className="font-anton uppercase text-5xl md:text-7xl leading-[0.85] text-white mb-4">
              Let's <span className="text-[#5cc6ff]">talk</span>
            </h2>
            <p className="font-manrope text-white/50 mb-12 max-w-md">
              Book. Collaborate. Enjoy.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="flex flex-col gap-8" data-testid="contact-form">
              <Field label="Your Name" placeholder="Jordan Alves" value={form.name} onChange={handle("name")} data-testid="contact-name" />
              <Field label="Email" type="email" placeholder="you@club.com" value={form.email} onChange={handle("email")} data-testid="contact-email" />
              <Field label="Subject" placeholder="Matchday coverage" value={form.subject} onChange={handle("subject")} data-testid="contact-subject" />
              <Field label="Message" textarea placeholder="Tell me about the shoot..." value={form.message} onChange={handle("message")} data-testid="contact-message" />

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex items-center justify-center gap-3 bg-[#5cc6ff] text-[#050505] font-outfit font-bold uppercase tracking-[0.15em] px-8 py-5 hover:bg-white transition-colors disabled:opacity-60 mt-2"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    Send Message
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
